import type {Menu} from '@/router/types';
import type {Ref} from 'vue';
import {watch, unref, ref, computed} from 'vue';
import {useRouter} from 'vue-router';
import {MenuSplitTyeEnum} from '@/enums/menuEnum';
import {useThrottleFn} from '@vueuse/core';
import {useMenuSetting} from '@/hooks/setting/useMenuSetting';
import {
  getChildrenMenus,
  getCurrentParentPath,
  getMenus,
  getShallowMenus,
  loadSystemMenus
} from '@/router/menus';
import {usePermissionStore} from '@/store/modules/permission';
import {useAppInject} from '@/hooks/web/useAppInject';
import {StorageUtils} from 'xmgoing-utils';
import {RoleEnum} from "@/enums/roleEnum";
import systemMenu from "@/layouts/data/systemMenu";

export function useSplitMenu(splitType: Ref<MenuSplitTyeEnum>,getNotShowAppMenuFlag:boolean) {
  // Menu array
  const menusRef = ref<Menu[]>([]);
  const {currentRoute} = useRouter();
  const {getIsMobile} = useAppInject();
  const permissionStore = usePermissionStore();
  const {setMenuSetting, getIsHorizontal, getSplit} = useMenuSetting();

  const throttleHandleSplitLeftMenu = useThrottleFn(handleSplitLeftMenu, 50);

  const splitNotLeft = computed(
    () => unref(splitType) !== MenuSplitTyeEnum.LEFT && !unref(getIsHorizontal),
  );

  const getSplitLeft = computed(
    () => !unref(getSplit) || unref(splitType) !== MenuSplitTyeEnum.LEFT,
  );

  const getSpiltTop = computed(() => unref(splitType) === MenuSplitTyeEnum.TOP);

  const normalType = computed(() => {
    return unref(splitType) === MenuSplitTyeEnum.NONE || !unref(getSplit);
  });

  watch(
    [() => unref(currentRoute).path, () => unref(splitType)],
    async ([path]: [string, MenuSplitTyeEnum]) => {
      if (unref(splitNotLeft) || unref(getIsMobile)) return;

      const {meta} = unref(currentRoute);
      const currentActiveMenu = meta.currentActiveMenu as string;
      let parentPath = await getCurrentParentPath(path);
      if (!parentPath) {
        parentPath = await getCurrentParentPath(currentActiveMenu);
      }
      parentPath && throttleHandleSplitLeftMenu(parentPath);
    },
    {
      immediate: true,
    },
  );

  // Menu changes
  watch(
    [() => permissionStore.getLastBuildMenuTime, () => permissionStore.getBackMenuList],
    () => {
      genMenus({});
    },
    {
      immediate: true,
    },
  );

  // split Menu changes
  watch(
    () => getSplit.value,
    () => {
      if (unref(splitNotLeft)) return;
      genMenus({});
    },
  );

  // Handle left menu split
  async function handleSplitLeftMenu(parentPath: string) {
    const position=StorageUtils.getSessionItem("loginUserType");
    if (unref(getSplitLeft) || unref(getIsMobile)) return;
    // spilt mode left
    const children = await getChildrenMenus(parentPath);
    if (!children || !children.length) {
      setMenuSetting({hidden: true});
      menusRef.value = [];
      return;
    }
    setMenuSetting({hidden: false});
    menusRef.value = children;
  }

  // get menus
  async function genMenus() {
    const position=StorageUtils.getSessionItem("loginUserType");
    //如果是系统管理员
    if(position===RoleEnum.USER_SYSTEM_ROLE){
      menusRef.value =systemMenu;
      loadSystemMenus(menusRef);
      // menusRef.value =systemMenu;
      return ;
    }
    // normal mode
    if (unref(normalType) || unref(getIsMobile)) {
      const shallowMenus = await getMenus();
      nextTick(() => {
        //如果打开了 不显示应用菜单标志
        if(unref(getNotShowAppMenuFlag)){
          let newOptions:any=[];
          shallowMenus.forEach((menuItem)=>{
            if(menuItem&&menuItem.children){
              newOptions=[...newOptions,...menuItem.children];
            }
          })
          menusRef.value = newOptions;
        }else{
          menusRef.value = shallowMenus;
        }
      })
      return;
    }

    // split-top
    if (unref(getSpiltTop)) {
      const shallowMenus = await getShallowMenus();
      nextTick(() => {
        menusRef.value = shallowMenus;
      })
      return;
    }
  }

  return {menusRef};
}
